import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Button,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  IconButton as MuiIconButton,
  Checkbox,
  FormControlLabel,
  FormGroup
} from '@mui/material';
import { Close, TableChart, Search, FilterList, Clear, ExpandMore, ViewColumn } from '@mui/icons-material';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import HelmSpinner from './HelmSpinner';
import { API_URL, fetchWithCredentials } from '../config';

const DatasetExplorer = ({ dataset, onClose, embedded = false }) => {
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [columnTypes, setColumnTypes] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [allColumns, setAllColumns] = useState([]);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loadedRecords, setLoadedRecords] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  
  // Muted color palette for enum chips (better readability)
  const enumColors = [
    '#66BB6A', // Muted green
    '#42A5F5', // Muted blue
    '#FFA726', // Muted orange
    '#EC407A', // Muted pink
    '#AB47BC', // Muted purple
    '#26C6DA', // Muted cyan
    '#D4E157', // Muted lime (instead of yellow)
    '#8D6E63', // Muted brown
    '#78909C', // Muted blue-grey
    '#FF7043'  // Muted deep orange
  ];
  
  const getEnumColor = (value, columnName) => {
    // Consistent color for each unique value
    const hash = (value + columnName).split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
    return enumColors[Math.abs(hash) % enumColors.length];
  };
  const [filters, setFilters] = useState({});

  useEffect(() => {
    if (dataset) {
      fetchPreviewData();
    }
  }, [dataset]);

  const fetchPreviewData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use the new dataset endpoint to get uploaded data
      const response = await fetchWithCredentials(`${API_URL}/api/dataset/${dataset.category}`);
      
      if (response.ok) {
        const data = await response.json();
        const fullData = data.data || [];
        setPreviewData(fullData);
        setTotalRecords(data.total_records || fullData.length);
        setLoadedRecords(fullData.length);
        setHasMore(data.has_more || false);
        
        // Detect enum/categorical columns (columns with <= 20 unique values)
        if (fullData.length > 0) {
          const types = {};
          const sampleSize = Math.min(fullData.length, 1000);
          const sample = fullData.slice(0, sampleSize);
          
          Object.keys(fullData[0] || {}).forEach(col => {
            // Skip date columns for enum detection
            const isDateColumn = col.toLowerCase().includes('date') || 
                                 col.toLowerCase().includes('time') ||
                                 col.toLowerCase().includes('week_start') ||
                                 col.toLowerCase().includes('month');
            
            const uniqueValues = new Set(sample.map(row => row[col]).filter(v => v !== null && v !== ''));
            
            if (isDateColumn) {
              types[col] = { type: 'date' };
            } else if (uniqueValues.size > 0 && uniqueValues.size <= 20) {
              types[col] = {
                type: 'enum',
                values: Array.from(uniqueValues),
                count: uniqueValues.size
              };
            } else if (uniqueValues.size > 20) {
              // Check if numeric
              const firstValue = Array.from(uniqueValues)[0];
              if (typeof firstValue === 'number' || !isNaN(parseFloat(firstValue))) {
                types[col] = { type: 'numeric' };
              } else {
                types[col] = { type: 'text' };
              }
            }
          });
          setColumnTypes(types);
        }
        
        // Initialize columns using explicit column order from backend
        if (data.columns && data.columns.length > 0) {
          // ALWAYS use the explicit column order from API (preserves CSV order)
          setAllColumns(data.columns);
          setVisibleColumns(data.columns);
          console.log('✅ Using API column order:', data.columns.slice(0, 5), '...');
        } else if (fullData.length > 0) {
          // Fallback if no columns array provided
          const columnOrder = Object.keys(fullData[0]);
          setAllColumns(columnOrder);
          setVisibleColumns(columnOrder);
          console.log('⚠️ Fallback to Object.keys order:', columnOrder.slice(0, 5), '...');
        }
        
        // Set default sort field
        if (fullData.length > 0) {
          const firstRow = fullData[0];
          const skuField = Object.keys(firstRow).find(key => 
            key.includes('sku') || key.includes('matnr') || key.includes('id')
          );
          setSortField(skuField || Object.keys(firstRow)[0]);
        }
      } else if (response.status === 404) {
        // No uploaded dataset, try fallback to original data
        let fallbackEndpoint = '';
        if (dataset.category === 'product_info') {
          fallbackEndpoint = '/api/products';
        } else if (dataset.category === 'sell_through') {
          fallbackEndpoint = '/api/sellout';
        }
        
        if (fallbackEndpoint) {
          const fallbackResponse = await fetchWithCredentials(`${API_URL}${fallbackEndpoint}`);
          const fallbackData = await fallbackResponse.json();
          setPreviewData(fallbackData.data || []);
        }
      } else {
        throw new Error(`Failed to fetch dataset: ${response.statusText}`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Sorting and filtering logic
  const handleSort = (field) => {
    const isAsc = sortField === field && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortField(field);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  // Apply filters and search
  const filteredData = previewData?.filter(item => {
    // Global search filter
    const matchesSearch = searchTerm === '' || 
      Object.values(item).some(value =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );
    
    // Column-specific filters
    const matchesColumnFilters = Object.entries(columnFilters).every(([column, filterValue]) => {
      if (filterValue === '' || filterValue === undefined) return true;
      
      const itemValue = item[column];
      const colType = columnTypes[column];
      
      // Enum type - exact match
      if (colType?.type === 'enum') {
        return String(itemValue) === String(filterValue);
      }
      
      // Numeric type - support comparisons (>100, <50, >=10, <=20)
      if (colType?.type === 'numeric' && typeof filterValue === 'string') {
        const numValue = parseFloat(itemValue);
        if (isNaN(numValue)) return false;
        
        // Parse comparison operators
        if (filterValue.startsWith('>=')) {
          return numValue >= parseFloat(filterValue.substring(2));
        } else if (filterValue.startsWith('<=')) {
          return numValue <= parseFloat(filterValue.substring(2));
        } else if (filterValue.startsWith('>')) {
          return numValue > parseFloat(filterValue.substring(1));
        } else if (filterValue.startsWith('<')) {
          return numValue < parseFloat(filterValue.substring(1));
        } else if (filterValue.startsWith('=')) {
          return numValue === parseFloat(filterValue.substring(1));
        } else {
          // Default: contains for numeric converted to string
          return String(numValue).includes(filterValue);
        }
      }
      
      // Text type - contains (case insensitive)
      return String(itemValue || '').toLowerCase().includes(String(filterValue).toLowerCase());
    });
    
    return matchesSearch && matchesColumnFilters;
  }) || [];

  // Apply sorting
  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortField) return 0;
    
    let aValue = a[sortField] || '';
    let bValue = b[sortField] || '';
    
    // Handle numeric fields
    if (!isNaN(aValue) && !isNaN(bValue)) {
      aValue = parseFloat(aValue) || 0;
      bValue = parseFloat(bValue) || 0;
    } else {
      aValue = aValue.toString().toLowerCase();
      bValue = bValue.toString().toLowerCase();
    }
    
    if (sortDirection === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Generate rich summary statistics like main tabs
  const generateSummary = () => {
    if (!previewData || previewData.length === 0) return null;
    
    const firstRow = previewData[0];
    const columns = Object.keys(firstRow);
    
    // Detect column types
    const numericColumns = columns.filter(key => !isNaN(firstRow[key]) && firstRow[key] !== '');
    const categoryColumns = columns.filter(key => isNaN(firstRow[key]));
    
    // Calculate rich statistics
    const summary = {
      total_records: previewData.length,
      filtered_records: filteredData.length,
      columns_count: columns.length,
      numeric_columns: numericColumns.length
    };
    
    // Add category-specific metrics
    if (dataset.category === 'product_info') {
      // Product-specific metrics
      const brandColumn = columns.find(col => col.toLowerCase().includes('brand'));
      const categoryColumn = columns.find(col => col.toLowerCase().includes('category'));
      const departmentColumn = columns.find(col => col.toLowerCase().includes('department'));
      
      if (brandColumn) {
        summary.unique_brands = [...new Set(previewData.map(row => row[brandColumn]).filter(Boolean))].length;
      }
      if (categoryColumn) {
        summary.unique_categories = [...new Set(previewData.map(row => row[categoryColumn]).filter(Boolean))].length;
      }
      if (departmentColumn) {
        summary.unique_departments = [...new Set(previewData.map(row => row[departmentColumn]).filter(Boolean))].length;
      }
    } else {
      // Sales/transaction-specific metrics
      const amountColumn = numericColumns.find(col => col.toLowerCase().includes('amount') || col.toLowerCase().includes('revenue'));
      const qtyColumn = numericColumns.find(col => col.toLowerCase().includes('qty') || col.toLowerCase().includes('units'));
      const storeColumn = columns.find(col => col.toLowerCase().includes('store') || col.toLowerCase().includes('channel'));
      
      if (amountColumn) {
        summary.total_revenue = previewData.reduce((sum, row) => sum + (parseFloat(row[amountColumn]) || 0), 0);
        summary.avg_transaction = summary.total_revenue / previewData.length;
      }
      if (qtyColumn) {
        summary.total_units = previewData.reduce((sum, row) => sum + (parseFloat(row[qtyColumn]) || 0), 0);
      }
      if (storeColumn) {
        summary.unique_channels = [...new Set(previewData.map(row => row[storeColumn]).filter(Boolean))].length;
      }
    }
    
    return summary;
  };

  // Generate multiple chart datasets
  const generateChartData = () => {
    if (!previewData || previewData.length === 0) return null;
    
    const firstRow = previewData[0];
    const columns = Object.keys(firstRow);
    
    // Find different types of columns
    const numericColumns = columns.filter(key => !isNaN(firstRow[key]) && firstRow[key] !== '');
    const categoryColumns = columns.filter(key => isNaN(firstRow[key]) && key !== 'id');
    const dateColumns = columns.filter(key => key.toLowerCase().includes('date') || key.toLowerCase().includes('week'));
    
    const charts = {};
    
    // Chart 1: Category distribution (if we have categories)
    if (categoryColumns.length > 0) {
      const categoryColumn = categoryColumns[0];
      const categoryGroups = {};
      
      previewData.forEach(row => {
        const category = row[categoryColumn];
        if (category) {
          categoryGroups[category] = (categoryGroups[category] || 0) + 1;
        }
      });
      
      charts.categoryDistribution = {
        data: Object.entries(categoryGroups)
          .sort(([,a], [,b]) => b - a)
          .slice(0, 12)
          .map(([category, count]) => ({
            name: category.length > 20 ? category.substring(0, 20) + '...' : category,
            value: count,
            fullName: category
          })),
        title: `Distribution by ${categoryColumn.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`,
        description: `Count of records by ${categoryColumn}`,
        type: 'bar'
      };
    }
    
    // Chart 2: Numeric distribution (if we have numeric data)
    if (numericColumns.length > 0 && categoryColumns.length > 0) {
      const numericColumn = numericColumns[0];
      const categoryColumn = categoryColumns[0];
      const numericGroups = {};
      
      previewData.forEach(row => {
        const category = row[categoryColumn];
        const value = parseFloat(row[numericColumn]) || 0;
        
        if (category && value > 0) {
          numericGroups[category] = (numericGroups[category] || 0) + value;
        }
      });
      
      charts.numericDistribution = {
        data: Object.entries(numericGroups)
          .sort(([,a], [,b]) => b - a)
          .slice(0, 10)
          .map(([category, total]) => ({
            name: category.length > 15 ? category.substring(0, 15) + '...' : category,
            value: total,
            fullName: category
          })),
        title: `${numericColumn.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} by ${categoryColumn.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`,
        description: `Sum of ${numericColumn} grouped by ${categoryColumn}`,
        type: 'horizontal_bar'
      };
    }
    
    // Chart 3: Time series (if we have date/week columns)
    if (dateColumns.length > 0 && numericColumns.length > 0) {
      const dateColumn = dateColumns[0];
      const numericColumn = numericColumns[0];
      const timeGroups = {};
      
      previewData.forEach(row => {
        const timeKey = row[dateColumn];
        const value = parseFloat(row[numericColumn]) || 0;
        
        if (timeKey) {
          timeGroups[timeKey] = (timeGroups[timeKey] || 0) + value;
        }
      });
      
      charts.timeSeries = {
        data: Object.entries(timeGroups)
          .sort(([a], [b]) => a.localeCompare(b))
          .slice(0, 20)
          .map(([time, total]) => ({
            name: time,
            value: total
          })),
        title: `${numericColumn.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Over Time`,
        description: `Trend of ${numericColumn} by ${dateColumn}`,
        type: 'line'
      };
    }
    
    return charts;
  };

  // Get filterable columns (those with reasonable number of unique values)
  const getFilterableColumns = () => {
    if (!previewData || previewData.length === 0) return [];
    
    const columns = Object.keys(previewData[0]);
    return columns.filter(column => {
      const uniqueValues = [...new Set(previewData.slice(0, 1000).map(row => row[column]).filter(Boolean))];
      return uniqueValues.length > 1 && uniqueValues.length <= 50; // Good for dropdowns
    });
  };

  const getUniqueValuesForColumn = (column) => {
    if (!previewData) return [];
    return [...new Set(previewData.slice(0, 1000).map(row => row[column]).filter(Boolean))].sort();
  };

  const summary = generateSummary();
  const charts = generateChartData();
  const filterableColumns = getFilterableColumns();

  const isSalesData = ['sell_in', 'sell_through', 'productivity_style_week'].includes(dataset?.category);

  const renderDataTable = () => {
    if (!previewData || previewData.length === 0) return null;
    
    // Use visibleColumns as the source of truth (already in correct order)
    const displayColumns = visibleColumns;
    
    return (
      <TableContainer sx={{ maxHeight: 600, overflowX: 'auto' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'background.secondary' }}>
              {displayColumns.map((column) => (
                <TableCell 
                  key={column}
                  sortDirection={sortField === column ? sortDirection : false}
                  sx={{ fontWeight: 600, minWidth: 120 }}
                >
                  <TableSortLabel
                    active={sortField === column}
                    direction={sortDirection}
                    onClick={() => handleSort(column)}
                  >
                    {column.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, index) => (
              <TableRow key={index} hover>
                {displayColumns.map((column) => {
                  const value = row[column];
                  const columnType = columnTypes[column];
                  const isNumeric = !isNaN(value) && value !== '' && value !== null;
                  
                  // Render cell based on type
                  let cellContent;
                  
                  if (columnType?.type === 'enum' && value) {
                    // Render as outlined chip with colored border
                    const color = getEnumColor(String(value), column);
                    cellContent = (
                      <Chip
                        label={String(value)}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: color,
                          color: color,
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          backgroundColor: `${color}10` // 10% opacity background
                        }}
                      />
                    );
                  } else if (isNumeric) {
                    // Render numbers with special styling
                    const numValue = parseFloat(value);
                    cellContent = (
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          fontWeight: 600,
                          color: numValue < 0 ? 'error.main' : 'text.primary',
                          fontSize: '0.85rem'
                        }}
                      >
                        {typeof numValue === 'number' && !isNaN(numValue)
                          ? numValue.toLocaleString(undefined, { maximumFractionDigits: 2 })
                          : value}
                      </Typography>
                    );
                  } else {
                    // Regular text
                    cellContent = value || '';
                  }
                  
                  return (
                    <TableCell 
                      key={column}
                      align={isNumeric && columnType?.type !== 'enum' ? 'right' : 'left'}
                      sx={{ 
                        minWidth: 120,
                        whiteSpace: 'nowrap',
                        py: 1.5
                      }}
                    >
                      {cellContent}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderSalesPreview = () => (
    <TableContainer sx={{ maxHeight: 400 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Store Type</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Net Amount</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Unit Price</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {previewData?.slice(0, 20).map((transaction, index) => (
            <TableRow key={index} hover>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {transaction.short_sku}
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                  {transaction.billing_date}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip 
                  label={transaction.store_type} 
                  size="small" 
                  variant="outlined"
                  color={transaction.store_type === 'WEB' ? 'primary' : 'default'}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', textAlign: 'right' }}>
                  {parseFloat(transaction.tot_qty || 0).toLocaleString()}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', textAlign: 'right' }}>
                  ${parseFloat(transaction.tot_net_amount || 0).toFixed(2)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', textAlign: 'right' }}>
                  ${(parseFloat(transaction.tot_net_amount || 0) / parseFloat(transaction.tot_qty || 1)).toFixed(2)}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (!dataset) return null;

  return (
    <Box>
      {/* Header */}
      {!embedded && (
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TableChart />
              {dataset.title || dataset.name?.replace(/\.(csv|CSV)$/, '')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {dataset.type && `${dataset.type} • `}
              {dataset.row_count?.toLocaleString() || dataset.rowCount?.toLocaleString() || 0} records
            </Typography>
          </Box>
          {onClose && (
            <Button onClick={onClose} startIcon={<Close />} variant="outlined" color="error">
              Remove Dataset
            </Button>
          )}
        </Box>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, flexDirection: 'column' }}>
          <HelmSpinner size={64} showText={true} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading dataset...
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error loading dataset: {error}
        </Alert>
      )}

      {previewData && summary && (
        <>
          {/* Rich Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {dataset.category === 'product_info' ? (
              // Product-style summary cards
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={1}>
                    <CardContent>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {summary.total_records.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Products
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={1}>
                    <CardContent>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                        {summary.unique_brands || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Brands
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={1}>
                    <CardContent>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
                        {summary.unique_departments || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Departments
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={1}>
                    <CardContent>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'info.main' }}>
                        {summary.unique_categories || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Categories
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </>
            ) : (
              // Sales-style summary cards  
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={1}>
                    <CardContent>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {summary.filtered_records.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {summary.filtered_records !== summary.total_records ? 'Filtered ' : ''}Transactions
                      </Typography>
                      {summary.filtered_records !== summary.total_records && (
                        <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                          Filtered View
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={1}>
                    <CardContent>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                        ${summary.total_revenue ? (summary.total_revenue / 1000000).toFixed(1) + 'M' : '0'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {summary.filtered_records !== summary.total_records ? 'Filtered ' : ''}Revenue
                      </Typography>
                      {summary.filtered_records !== summary.total_records && (
                        <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                          Filtered View
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={1}>
                    <CardContent>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
                        ${summary.avg_transaction ? summary.avg_transaction.toFixed(2) : '0'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Avg Transaction Value
                      </Typography>
                      {summary.filtered_records !== summary.total_records && (
                        <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                          Filtered View
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={1}>
                    <CardContent>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'info.main' }}>
                        {summary.total_units ? summary.total_units.toLocaleString() : '0'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Units Sold
                      </Typography>
                      {summary.filtered_records !== summary.total_records && (
                        <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                          Filtered View
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </>
            )}
          </Grid>

          {/* Multiple Charts */}
          {charts && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {charts.categoryDistribution && (
                <Grid item xs={12} md={6}>
                  <Paper elevation={1} sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      {charts.categoryDistribution.title}
                    </Typography>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={charts.categoryDistribution.data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="name" 
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          tick={{ fontSize: 10 }}
                        />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip 
                          formatter={(value, name) => [value.toLocaleString(), 'Count']}
                          labelFormatter={(label) => charts.categoryDistribution.data.find(d => d.name === label)?.fullName || label}
                        />
                        <Bar dataKey="value" fill="#47BDC6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
              )}
              
              {charts.numericDistribution && (
                <Grid item xs={12} md={6}>
                  <Paper elevation={1} sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      {charts.numericDistribution.title}
                    </Typography>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={charts.numericDistribution.data} layout="horizontal">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tick={{ fontSize: 10 }} />
                        <YAxis 
                          type="category"
                          dataKey="name" 
                          tick={{ fontSize: 9 }}
                          width={100}
                        />
                        <Tooltip 
                          formatter={(value, name) => [value.toLocaleString(), 'Total']}
                          labelFormatter={(label) => charts.numericDistribution.data.find(d => d.name === label)?.fullName || label}
                        />
                        <Bar dataKey="value" fill="#845BA3" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
              )}
              
              {charts.timeSeries && (
                <Grid item xs={12}>
                  <Paper elevation={1} sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      {charts.timeSeries.title}
                    </Typography>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={charts.timeSeries.data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="name" 
                          tick={{ fontSize: 10 }}
                          angle={-45}
                          textAnchor="end"
                          height={60}
                        />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip formatter={(value, name) => [value.toLocaleString(), 'Total']} />
                        <Line 
                          type="monotone" 
                          dataKey="value" 
                          stroke="#47BDC6" 
                          strokeWidth={3}
                          dot={{ fill: '#47BDC6', strokeWidth: 2, r: 4 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}

          {/* Search and Filters */}
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterList />
                Search & Filters
              </Typography>
              <Box display="flex" gap={1}>
                <Button
                  size="small"
                  startIcon={<ViewColumn />}
                  onClick={() => setShowColumnSelector(!showColumnSelector)}
                  variant={showColumnSelector ? 'contained' : 'outlined'}
                >
                  Columns ({visibleColumns.length}/{allColumns.length})
                </Button>
                {Object.keys(columnFilters).length > 0 && (
                  <Button
                    size="small"
                    startIcon={<Clear />}
                    onClick={() => {
                      setColumnFilters({});
                      setSearchTerm('');
                    }}
                  >
                    Clear All
                  </Button>
                )}
                <Button
                  size="small"
                  startIcon={<ExpandMore sx={{ transform: showFilters ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }} />}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  {showFilters ? 'Hide' : 'Show'} Filters
                </Button>
              </Box>
            </Box>
            
            {/* Global Search */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search across all columns..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Showing {paginatedData.length} of {filteredData.length} records
                    {filteredData.length !== summary.total_records && 
                      ` (filtered from ${summary.total_records.toLocaleString()} total)`
                    }
                  </Typography>
                  {hasMore && (
                    <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                      Loaded {loadedRecords.toLocaleString()} of {totalRecords.toLocaleString()} total rows
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
            
            {/* Column Selector */}
            <Collapse in={showColumnSelector}>
              <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider', mb: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Select Columns to Display
                  </Typography>
                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      onClick={() => setVisibleColumns(allColumns)}
                    >
                      Show All ({allColumns.length})
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setVisibleColumns([])}
                    >
                      Hide All
                    </Button>
                  </Box>
                </Box>
                <FormGroup row>
                  {/* Use allColumns to show all available columns in correct order */}
                  {allColumns.map(col => {
                    const colType = columnTypes[col];
                    return (
                    <FormControlLabel
                      key={col}
                      control={
                        <Checkbox
                          checked={visibleColumns.includes(col)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setVisibleColumns([...visibleColumns, col]);
                            } else {
                              setVisibleColumns(visibleColumns.filter(c => c !== col));
                            }
                          }}
                          size="small"
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                            {col}
                          </Typography>
                          {colType && (
                            <Chip 
                              label={colType.type} 
                              size="small" 
                              sx={{ height: 16, fontSize: '0.65rem' }}
                              color={colType.type === 'enum' ? 'primary' : colType.type === 'numeric' ? 'success' : 'default'}
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      sx={{ mr: 2, mb: 0.5 }}
                    />
                    );
                  })}
                </FormGroup>
              </Box>
            </Collapse>
            
            {/* Column-Specific Filters */}
            <Collapse in={showFilters}>
              <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                  Filter by Column
                </Typography>
                <Grid container spacing={2}>
                  {previewData && visibleColumns.slice(0, 12).map(col => {
                    const colType = columnTypes[col];
                    
                    return (
                      <Grid item xs={12} sm={6} md={3} key={col}>
                        {colType?.type === 'enum' ? (
                          <FormControl fullWidth size="small">
                            <InputLabel sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {col}
                            </InputLabel>
                            <Select
                              value={columnFilters[col] || ''}
                              label={col}
                              onChange={(e) => setColumnFilters({...columnFilters, [col]: e.target.value})}
                              MenuProps={{
                                PaperProps: {
                                  sx: { maxHeight: 300 }
                                }
                              }}
                            >
                              <MenuItem value="">
                                <em>All {col}</em>
                              </MenuItem>
                              {colType.values.map(val => {
                                const color = getEnumColor(String(val), col);
                                return (
                                  <MenuItem key={val} value={val}>
                                    <Chip 
                                      label={val}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        borderColor: color,
                                        color: color,
                                        backgroundColor: `${color}15`,
                                        fontSize: '0.75rem',
                                        maxWidth: 250,
                                        fontWeight: 500
                                      }}
                                    />
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                        ) : (
                          <TextField
                            fullWidth
                            size="small"
                            label={col}
                            value={columnFilters[col] || ''}
                            onChange={(e) => setColumnFilters({...columnFilters, [col]: e.target.value})}
                            placeholder={colType?.type === 'numeric' ? '>100 or <50' : 'Contains...'}
                            InputLabelProps={{
                              sx: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 'calc(100% - 24px)' }
                            }}
                          />
                        )}
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Collapse>
          </Paper>

          {/* Data Table */}
          <Paper elevation={1}>
            {renderDataTable()}
            
            <TablePagination
              component="div"
              count={sortedData.length}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Paper>
        </>
      )}
    </Box>
  );
};

export default DatasetExplorer;
